import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { checkInputErrorFileMaxSize, useFile, usePathParam, useViewport } from '@chilecompra/react-kit'
import { File, Spinner, Toast, Typography } from '@chilecompra/react-kit/components'
import { REACT_APP_FEATURE_FLAG_SHOW_MIGRATED_FILES } from '../../config/settings/environment'
import { onDownloadRevokeFileThunk, onDownloadSanctionFileThunk, onSelectSanctionAttachment } from './Sanction.actions'
import {
  Box,
  Column,
  DownloadButton,
  FileIcon,
  InfoIcon,
  Link,
  Loading,
  Row,
  Separator
} from './SanctionAttachments.styles'
import { useLocation } from 'react-router-dom'
import { USER_TYPE } from '../../config/settings/constant'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

/**
 * The Sanction Attachments' container.
 */
const SanctionAttachments = () => {
  const dispatch = useDispatch()
  const { userType } = useAuthorization()
  const sanctionID = usePathParam('id')
  const { search } = useLocation()
  const {
    size: { isTiny, isUpToMedium }
  } = useViewport()
  const {
    downloading,
    downloadError,
    attachmentsWS,
    downloadingWS,
    downloadWSError,
    form: {
      attachment: { file, fileName, isMigrated },
      isSGC,
      contractUrl
    },
    poBiddingInfo: { error: purchaseOrderError, loading: purchaseOrderLoading },
    userInfo: { error: userInfoError }
  } = useSelector(state => state.sanction)
  const { error: sellerError } = useSelector(state => state.basicSellerInformation)

  const {
    error: fileError,
    value: fileValue,
    onChange: handleFileChange
  } = useFile({
    errorCallbacks: [checkInputErrorFileMaxSize(20)],
    initialValue: file,
    changeCallback: file =>
      dispatch(
        onSelectSanctionAttachment({
          attachment: {
            fileName: file?.name,
            file,
            applicationType: file?.type
          }
        })
      ),
    overwriteCallback: () => file
  })

  const handleDownload = () => {
    dispatch(onDownloadSanctionFileThunk())
  }
  const handleDownloadRevoke = attachment => {
    dispatch(onDownloadRevokeFileThunk({ atcCode: attachment.atcCode, fileName: attachment.fileName }))
  }
  const canDownload =
    !isMigrated ||
    (isMigrated &&
      (REACT_APP_FEATURE_FLAG_SHOW_MIGRATED_FILES === true || REACT_APP_FEATURE_FLAG_SHOW_MIGRATED_FILES === 'true'))

  return userInfoError || purchaseOrderError || purchaseOrderLoading || sellerError ? null : (
    <Row margin="0 0 2rem">
      <Column xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 0 0' : '0'}>
        {isNaN(sanctionID) && (
          <Box width={isTiny ? '100%' : '500px'}>
            <File
              accept="application/pdf"
              browseText={fileValue === undefined || fileValue === null ? 'Seleccionar archivo' : 'Reemplazar archivo'}
              error={fileError}
              info="Archivos permitidos: Formato PDF, peso máximo 20mb"
              label="Documentación"
              value={fileValue}
              onChange={handleFileChange}
            />
          </Box>
        )}

        {!isNaN(sanctionID) && canDownload && !search && !isSGC && userType !== USER_TYPE.PUBLIC && (
          <DownloadButton disabled={downloading} variant="text" onClick={handleDownload}>
            {downloading ? (
              <Loading>
                <Spinner />
              </Loading>
            ) : (
              <>
                <FileIcon />
                {fileName}
              </>
            )}
          </DownloadButton>
        )}
        {!isNaN(sanctionID) && canDownload && !search && isSGC && (
          <Row alignItems="center">
            <InfoIcon />
            <Typography fontSize="14px" variant="body2">
              {`Para conocer más detalles, consulta la documentación adjunta en `}
              <Link href={contractUrl} target="_blank" rel="noreferrer">
                Ficha de Contrato
              </Link>
            </Typography>
          </Row>
        )}
        {!isNaN(sanctionID) && !canDownload && (
          <>
            <Typography color="black3" padding="0 0 28px" variant="body2">
              Archivo no disponible
            </Typography>
          </>
        )}

        {search && (
          <Toast align="left" padding="12px 50px 12px 30px" severity="warning">
            Si necesitas ver el documento de respaldo de esta sanción, puedes solicitarlo a través de nuestra mesa de
            ayuda.
          </Toast>
        )}

        {!downloading && downloadError && (
          <Toast
            align="left"
            border
            margin="12px auto 24px"
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
          >
            En estos momentos tenemos problemas para descargar el archivo solicitado. Por favor, intenta en unos minutos
            más.
          </Toast>
        )}

        {!isNaN(sanctionID) && attachmentsWS?.length > 0 && (
          <>
            <Typography fontWeight="bold" margin="8px 0" variant="body2">
              Documento de respaldo para dejar sin efecto la sanción
            </Typography>
            {attachmentsWS.map(attachment => (
              <DownloadButton
                key={attachment.atcCode}
                disabled={downloadingWS === attachment.atcCode}
                variant="text"
                onClick={() => handleDownloadRevoke(attachment)}
              >
                {downloadingWS === attachment.atcCode ? (
                  <Loading>
                    <Spinner />
                  </Loading>
                ) : (
                  <>
                    <FileIcon />
                    {attachment.fileName}
                  </>
                )}
              </DownloadButton>
            ))}
          </>
        )}

        {!downloadingWS && downloadWSError && (
          <Toast
            align="left"
            border
            margin="12px auto 24px"
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
          >
            En estos momentos tenemos problemas para descargar el documento de respaldo. Por favor, intenta en unos
            minutos más.
          </Toast>
        )}

        <Separator />
      </Column>
    </Row>
  )
}

export default SanctionAttachments
